import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeApi } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { Repeat, DollarSign, Coins, Lock, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const EXCHANGE_RATE = 74207381348100;

export default function Exchange() {
  const [direction, setDirection] = useState<'toMS' | 'toNT'>('toMS');
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const calculateResult = () => {
    const numAmount = parseFloat(amount) || 0;
    if (direction === 'toMS') {
      return (numAmount * EXCHANGE_RATE).toLocaleString();
    } else {
      return (numAmount / EXCHANGE_RATE).toFixed(2);
    }
  };

  const handleExchange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !password) {
      toast.error('請填寫所有必填欄位');
      return;
    }
    setLoading(true);
    try {
      if (direction === 'toMS') {
        await exchangeApi.toMS({ amount: parseFloat(amount), password });
        toast.success('兌換成功！已將新臺幣轉換為數資幣');
      } else {
        await exchangeApi.toNT({ amount: parseFloat(amount), password });
        toast.success('兌換成功！已將數資幣轉換為新臺幣');
      }
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.error || '兌換失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900">貨幣兌換</h1>
        <p className="text-slate-500 mt-2">新臺幣與數資幣互相兌換</p>
      </div>

      {/* Exchange Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        {/* Direction Selector */}
        <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => { setDirection('toMS'); setAmount(''); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                direction === 'toMS' 
                  ? 'bg-white shadow-lg ring-2 ring-blue-500' 
                  : 'hover:bg-white/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                direction === 'toMS' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                <DollarSign className="h-6 w-6" />
              </div>
              <span className="font-medium text-slate-700">NTD</span>
            </button>

            <div className="flex flex-col items-center">
              <ArrowRight className={`h-6 w-6 text-slate-400 transition-transform ${
                direction === 'toNT' ? 'rotate-180' : ''
              }`} />
              <span className="text-xs text-slate-500 mt-1">兌換</span>
            </div>

            <button
              onClick={() => { setDirection('toNT'); setAmount(''); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                direction === 'toNT' 
                  ? 'bg-white shadow-lg ring-2 ring-purple-500' 
                  : 'hover:bg-white/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                direction === 'toNT' ? 'bg-purple-500 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                <Coins className="h-6 w-6" />
              </div>
              <span className="font-medium text-slate-700">MS∮</span>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Exchange Rate Info */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Repeat className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">固定匯率</span>
            </div>
            <p className="text-lg font-bold text-slate-900">
              1 NTD = {EXCHANGE_RATE.toLocaleString()} MS∮
            </p>
            <p className="text-xs text-slate-500 mt-1">
              每日兌換上限: {user?.dailyLimit || 50} NTD
            </p>
          </div>

          {/* Balance Display */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs text-blue-600 font-medium mb-1">新臺幣餘額</p>
              <p className="text-xl font-bold text-slate-900">
                ${user?.ntBalance?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-xs text-purple-600 font-medium mb-1">數資幣餘額</p>
              <p className="text-xl font-bold text-slate-900">
                {user?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 0}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleExchange} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                兌換金額 ({direction === 'toMS' ? 'NTD' : 'MS∮'})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">
                  {direction === 'toMS' ? '$' : '∮'}
                </span>
                <input
                  type="number"
                  className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`請輸入${direction === 'toMS' ? '新臺幣' : '數資幣'}金額`}
                  min="1"
                  step={direction === 'toNT' ? '1' : '0.01'}
                  required
                />
              </div>
              {amount && (
                <p className="text-sm text-slate-500 mt-2">
                  約可兌換 <span className="font-bold text-blue-600">{calculateResult()}</span> {direction === 'toMS' ? 'MS∮' : 'NTD'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">確認密碼</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="password"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="請輸入您的密碼確認"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 font-semibold rounded-xl shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                direction === 'toMS'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-500/25'
                  : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-purple-500/25'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  處理中...
                </>
              ) : (
                <>
                  <Repeat className="h-5 w-5" />
                  {direction === 'toMS' ? '兌換成數資幣' : '兌換成新臺幣'}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
